import { Component, OnInit, OnDestroy } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AgGridModule } from 'ag-grid-angular';
import { BaseChartDirective } from 'ng2-charts';
import { ChartConfiguration, ChartData } from 'chart.js';
import { Subject, combineLatest, of } from 'rxjs';
import { takeUntil, catchError, finalize } from 'rxjs/operators';
import { OwnershipService } from '../services/ownership.service';
import { EnvironmentService } from '../services/environment.service';
import { LoadingService } from '../services/loading.service';
import { OwnershipEntry } from '../models/ownership-entry';
import { TeamOwnerMinorEntry } from '../models/team-owner-minor-entry';
import { Environment } from '../models/environment';

interface OverviewStats {
  totalRepos: number;
  totalReleasePipelines: number;
  totalBuildDefinitions: number;
  totalYamlPipelines: number;
  stalePipelines: number;
}

interface RepositoryRow {
  repository: string;
  team: string;
  location: string;
  systems: string[];
  releasePipelines: number;
  buildDefinitions: number;
  yamlPipelines: number;
  lastCommitDate: string;
  hasPipelines: boolean;
  ownershipEntry: OwnershipEntry;
}

interface PipelineRow {
  name: string;
  type: 'Release' | 'Build' | 'YAML';
  repository: string;
  team: string;
  systems: string[];
  lastRunTime: string;
  daysAgo: number | null;
}

interface TeamStatsRow {
  team: string;
  repositories: number;
  releasePipelines: number;
  buildDefinitions: number;
  yamlPipelines: number;
  totalPipelines: number;
}

interface SystemStatsRow {
  system: string;
  repositories: number;
  releasePipelines: number;
  buildDefinitions: number;
  yamlPipelines: number;
  totalPipelines: number;
}

@Component({
  selector: 'app-ownership-stats',
  standalone: true,
  imports: [
    FormsModule,
    MatCardModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    AgGridModule,
    BaseChartDirective
],
  templateUrl: './ownership-stats.component.html',
  styleUrls: ['./ownership-stats.component.css']
})
export class OwnershipStatsComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  // Environment and organization
  environment!: Environment;
  org!: string;

  // Data
  ownershipData: OwnershipEntry[] = [];
  teamData: TeamOwnerMinorEntry[] = [];

  // UI State
  isLoading = true;
  error: string | null = null;
  selectedTabIndex = 0;

  // Processed data for display
  overviewStats: OverviewStats = {
    totalRepos: 0,
    totalReleasePipelines: 0,
    totalBuildDefinitions: 0,
    totalYamlPipelines: 0,
    stalePipelines: 0
  };

  repositoryRows: RepositoryRow[] = [];
  pipelineRows: PipelineRow[] = [];
  teamStatsRows: TeamStatsRow[] = [];
  systemStatsRows: SystemStatsRow[] = [];
  stalePipelineRows: PipelineRow[] = [];

  // Filters
  selectedTeam = 'all';
  selectedLocation = 'all';
  selectedSystems: string[] = [];
  searchQuery = '';
  selectedPipelineType = 'all';
  pipelineAgeFilter = 0;
  hideNeverRun = false;

  // Filter options
  availableTeams: string[] = [];
  availableLocations: string[] = [];
  availableSystems: string[] = [];

  // Chart data for Team Stats
  public teamChartType = 'bar' as const;
  public teamChartData: ChartData<'bar'> = {
    labels: [],
    datasets: []
  };
  public teamChartOptions: ChartConfiguration<'bar'>['options'] = {
    responsive: true,
    scales: {
      x: {
        beginAtZero: true
      },
      y: {
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        display: true,
        position: 'top'
      },
      title: {
        display: true,
        text: 'Team Repository and Pipeline Statistics'
      }
    }
  };

  // ag-grid column definitions
  repositoryColumnDefs = [
    {
      field: 'repository' as keyof RepositoryRow,
      headerName: 'Repository',
      sortable: true,
      filter: true,
      flex: 2
    },
    {
      field: 'team' as keyof RepositoryRow,
      headerName: 'Team',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'location' as keyof RepositoryRow,
      headerName: 'Location',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'systems' as keyof RepositoryRow,
      headerName: 'Systems',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: (params: any) => params.value && params.value.length > 0 ? params.value.join(', ') : 'No Systems'
    },
    {
      field: 'releasePipelines' as keyof RepositoryRow,
      headerName: 'Release Pipelines',
      sortable: true,
      width: 150,
      cellClass: 'text-center'
    },
    {
      field: 'buildDefinitions' as keyof RepositoryRow,
      headerName: 'Build Definitions',
      sortable: true,
      width: 150,
      cellClass: 'text-center'
    },
    {
      field: 'yamlPipelines' as keyof RepositoryRow,
      headerName: 'YAML Pipelines',
      sortable: true,
      width: 140,
      cellClass: 'text-center'
    },
    {
      field: 'lastCommitDate' as keyof RepositoryRow,
      headerName: 'Last Commit',
      sortable: true,
      width: 120
    }
  ];

  pipelineColumnDefs = [
    {
      field: 'name' as keyof PipelineRow,
      headerName: 'Pipeline Name',
      sortable: true,
      filter: true,
      flex: 2
    },
    {
      field: 'type' as keyof PipelineRow,
      headerName: 'Type',
      sortable: true,
      filter: true,
      width: 100
    },
    {
      field: 'repository' as keyof PipelineRow,
      headerName: 'Repository',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'team' as keyof PipelineRow,
      headerName: 'Team',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'systems' as keyof PipelineRow,
      headerName: 'Systems',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: (params: any) => params.value && params.value.length > 0 ? params.value.join(', ') : 'No Systems'
    },
    {
      field: 'lastRunTime' as keyof PipelineRow,
      headerName: 'Last Run',
      sortable: true,
      width: 120
    },
    {
      field: 'daysAgo' as keyof PipelineRow,
      headerName: 'Days Ago',
      sortable: true,
      width: 100,
      cellClass: 'text-center'
    }
  ];

  teamStatsColumnDefs = [
    {
      field: 'team' as keyof TeamStatsRow,
      headerName: 'Team',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'repositories' as keyof TeamStatsRow,
      headerName: 'Repositories',
      sortable: true,
      width: 120,
      cellClass: 'text-center'
    },
    {
      field: 'releasePipelines' as keyof TeamStatsRow,
      headerName: 'Release Pipelines',
      sortable: true,
      width: 150,
      cellClass: 'text-center'
    },
    {
      field: 'buildDefinitions' as keyof TeamStatsRow,
      headerName: 'Build Definitions',
      sortable: true,
      width: 150,
      cellClass: 'text-center'
    },
    {
      field: 'yamlPipelines' as keyof TeamStatsRow,
      headerName: 'YAML Pipelines',
      sortable: true,
      width: 140,
      cellClass: 'text-center'
    },
    {
      field: 'totalPipelines' as keyof TeamStatsRow,
      headerName: 'Total Pipelines',
      sortable: true,
      width: 130,
      cellClass: 'text-center'
    }
  ];

  systemStatsColumnDefs = [
    {
      field: 'system' as keyof SystemStatsRow,
      headerName: 'System',
      sortable: true,
      filter: true,
      flex: 1
    },
    {
      field: 'repositories' as keyof SystemStatsRow,
      headerName: 'Repositories',
      sortable: true,
      width: 120,
      cellClass: 'text-center'
    },
    {
      field: 'releasePipelines' as keyof SystemStatsRow,
      headerName: 'Release Pipelines',
      sortable: true,
      width: 150,
      cellClass: 'text-center'
    },
    {
      field: 'buildDefinitions' as keyof SystemStatsRow,
      headerName: 'Build Definitions',
      sortable: true,
      width: 150,
      cellClass: 'text-center'
    },
    {
      field: 'yamlPipelines' as keyof SystemStatsRow,
      headerName: 'YAML Pipelines',
      sortable: true,
      width: 140,
      cellClass: 'text-center'
    },
    {
      field: 'totalPipelines' as keyof SystemStatsRow,
      headerName: 'Total Pipelines',
      sortable: true,
      width: 130,
      cellClass: 'text-center'
    }
  ];

  constructor(
    private readonly ownershipService: OwnershipService,
    private readonly environmentService: EnvironmentService,
    private readonly loadingService: LoadingService
  ) {}

  ngOnInit(): void {
    this.initializeEnvironment();
    this.loadData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeEnvironment(): void {
    this.environment = this.environmentService.getEnvironment();
    this.org = this.environment.defaultOrg;
  }

  private loadData(): void {
    this.isLoading = true;
    this.error = null;
    this.loadingService.show('ownership-data');

    combineLatest([
      this.ownershipService.getRepoOwnershipMaster(this.org),
      this.ownershipService.getTeamOwnerMinor(this.org)
    ]).pipe(
      takeUntil(this.destroy$),
      catchError(error => {
        console.error('Error loading ownership data:', error);
        this.error = 'Failed to load ownership data. Please try again.';
        return of([[], []]);
      }),
      finalize(() => {
        this.isLoading = false;
        this.loadingService.hide('ownership-data');
      })
    ).subscribe(([ownershipData, teamData]) => {
      this.ownershipData = ownershipData;
      this.teamData = teamData;
      this.processData();
    });
  }

  private processData(): void {
    this.processRepositoryData();
    this.processPipelineData();
    this.processTeamStatsData();
    this.processSystemStatsData();
    this.calculateOverviewStats();
    this.setupFilterOptions();
  }

  private calculateDaysAgo(dateString: string | null | undefined): number | null {
    if (!dateString) {
      return null;
    }

    try {
      // Check for sentinel "never run" values first (before parsing)
      if (dateString.includes('1900-01-01') ||
          dateString.includes('1900') ||
          dateString === 'Never' ||
          dateString === 'N/A') {
        return null;
      }

      const lastRunDate = new Date(dateString);

      // Check if this is an invalid date or before 1970 (Unix epoch start)
      if (isNaN(lastRunDate.getTime()) || lastRunDate.getFullYear() < 1970) {
        return null;
      }

      const now = new Date();
      const daysDiff = Math.floor((now.getTime() - lastRunDate.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 ? daysDiff : null;
    } catch (error) {
      return null;
    }
  }

  private processRepositoryData(): void {
    this.repositoryRows = this.ownershipData.map(entry => ({
      repository: entry.repository,
      team: entry.team,
      location: entry.location!,
      systems: entry.systems || [],
      releasePipelines: entry.releaseDefinitions?.length || 0,
      buildDefinitions: entry.buildDefinitions?.length || 0,
      yamlPipelines: entry.yamlPipelines?.length || 0,
      lastCommitDate: entry.lastCommitDate,
      hasPipelines: (entry.releaseDefinitions?.length || 0) +
                   (entry.buildDefinitions?.length || 0) +
                   (entry.yamlPipelines?.length || 0) > 0,
      ownershipEntry: entry
    }));
  }

  private processPipelineData(): void {
    this.pipelineRows = [];

    this.ownershipData.forEach(repo => {
      // Release Definitions
      if (repo.releaseDefinitions) {
        repo.releaseDefinitions.forEach(pipeline => {
          this.pipelineRows.push({
            name: pipeline.name,
            type: 'Release',
            repository: repo.repository,
            team: repo.team,
            systems: repo.systems || [],
            lastRunTime: pipeline.lastRunTime || 'Never',
            daysAgo: this.calculateDaysAgo(pipeline.lastRunTime),
          });
        });
      }

      // Build Definitions
      if (repo.buildDefinitions) {
        repo.buildDefinitions.forEach(pipeline => {
          this.pipelineRows.push({
            name: pipeline.name,
            type: 'Build',
            repository: repo.repository,
            team: repo.team,
            systems: repo.systems || [],
            lastRunTime: pipeline.lastRunTime || 'Never',
            daysAgo: this.calculateDaysAgo(pipeline.lastRunTime),
          });
        });
      }

      // YAML Pipelines
      if (repo.yamlPipelines) {
        repo.yamlPipelines.forEach(pipeline => {
          this.pipelineRows.push({
            name: pipeline.name,
            type: 'YAML',
            repository: repo.repository,
            team: repo.team,
            systems: repo.systems || [],
            lastRunTime: pipeline.lastRunTime || 'Never',
            daysAgo: this.calculateDaysAgo(pipeline.lastRunTime),
          });
        });
      }
    });

    // Get stale pipelines based on lastRunTime - pipelines that haven't run in 90+ days
    // Exclude pipelines that have never been run (1900-01-01 dates)
    const staleThresholdDays = 90;
    const stalePipelines = this.pipelineRows.filter(pipeline => {
      return pipeline.daysAgo !== null && pipeline.daysAgo > staleThresholdDays;
    });

    // Sort by days ago (descending) and take top 10 for overview
    this.stalePipelineRows = stalePipelines
      .sort((a, b) => b.daysAgo! - a.daysAgo!)
      .slice(0, 10);
  }

  private processTeamStatsData(): void {
    const teamStats = new Map<string, TeamStatsRow>();

    this.ownershipData.forEach(repo => {
      if (!teamStats.has(repo.team)) {
        teamStats.set(repo.team, {
          team: repo.team,
          repositories: 0,
          releasePipelines: 0,
          buildDefinitions: 0,
          yamlPipelines: 0,
          totalPipelines: 0
        });
      }

      const stats = teamStats.get(repo.team)!;
      stats.repositories++;
      stats.releasePipelines += repo.releaseDefinitions?.length || 0;
      stats.buildDefinitions += repo.buildDefinitions?.length || 0;
      stats.yamlPipelines += repo.yamlPipelines?.length || 0;
      stats.totalPipelines = stats.releasePipelines + stats.buildDefinitions + stats.yamlPipelines;
    });

    this.teamStatsRows = Array.from(teamStats.values()).sort((a, b) => a.team.localeCompare(b.team));
    this.updateTeamChart();
  }

  private processSystemStatsData(): void {
    const systemStats = new Map<string, SystemStatsRow>();

    this.ownershipData.forEach(repo => {
      const repoSystems = repo.systems || [];

      // Handle repositories with no systems
      if (repoSystems.length === 0) {
        const noSystemKey = 'No Systems';
        if (!systemStats.has(noSystemKey)) {
          systemStats.set(noSystemKey, {
            system: noSystemKey,
            repositories: 0,
            releasePipelines: 0,
            buildDefinitions: 0,
            yamlPipelines: 0,
            totalPipelines: 0
          });
        }

        const stats = systemStats.get(noSystemKey)!;
        stats.repositories++;
        stats.releasePipelines += repo.releaseDefinitions?.length || 0;
        stats.buildDefinitions += repo.buildDefinitions?.length || 0;
        stats.yamlPipelines += repo.yamlPipelines?.length || 0;
        stats.totalPipelines = stats.releasePipelines + stats.buildDefinitions + stats.yamlPipelines;
      } else {
        // Handle repositories with systems (each system gets a count)
        repoSystems.forEach(system => {
          if (!systemStats.has(system)) {
            systemStats.set(system, {
              system: system,
              repositories: 0,
              releasePipelines: 0,
              buildDefinitions: 0,
              yamlPipelines: 0,
              totalPipelines: 0
            });
          }

          const stats = systemStats.get(system)!;
          stats.repositories++;
          stats.releasePipelines += repo.releaseDefinitions?.length || 0;
          stats.buildDefinitions += repo.buildDefinitions?.length || 0;
          stats.yamlPipelines += repo.yamlPipelines?.length || 0;
          stats.totalPipelines = stats.releasePipelines + stats.buildDefinitions + stats.yamlPipelines;
        });
      }
    });

    this.systemStatsRows = Array.from(systemStats.values()).sort((a, b) => a.system.localeCompare(b.system));
  }

  private calculateOverviewStats(): void {
    // Count stale pipelines based on actual pipeline run times (90+ days or never run)
    const staleThresholdDays = 90;
    const stalePipelineCount = this.pipelineRows.filter(pipeline => {
      return pipeline.daysAgo === null || pipeline.daysAgo > staleThresholdDays;
    }).length;

    this.overviewStats = {
      totalRepos: this.ownershipData.length,
      totalReleasePipelines: this.ownershipData.reduce((total, repo) =>
        total + (repo.releaseDefinitions?.length || 0), 0),
      totalBuildDefinitions: this.ownershipData.reduce((total, repo) =>
        total + (repo.buildDefinitions?.length || 0), 0),
      totalYamlPipelines: this.ownershipData.reduce((total, repo) =>
        total + (repo.yamlPipelines?.length || 0), 0),
      stalePipelines: stalePipelineCount
    };
  }

  private setupFilterOptions(): void {
    // Get unique teams
    this.availableTeams = [...new Set(this.ownershipData.map(repo => repo.team))]
      .sort((a, b) => a.localeCompare(b));

    // Get unique locations
    this.availableLocations = [...new Set(this.ownershipData
      .map(repo => repo.location)
      .filter((location): location is string => location !== undefined && location !== null))]
      .sort((a, b) => a.localeCompare(b));

    // Get unique systems
    this.availableSystems = Array.from(new Set(this.ownershipData
      .flatMap(repo => repo.systems || [])
      .filter((system): system is string => Boolean(system))))
      .sort((a, b) => a.localeCompare(b));
  }

  // Filter methods
  clearFilters(): void {
    this.selectedTeam = 'all';
    this.selectedLocation = 'all';
    this.selectedSystems = [];
    this.searchQuery = '';
    this.selectedPipelineType = 'all';
    this.pipelineAgeFilter = 0;
    this.hideNeverRun = false;
  }

  // Systems filter handler
  onSystemsFilterChange(selectedSystems: string[]): void {
    this.selectedSystems = selectedSystems;
  }

  // Tab change handler
  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
  }

  // Update team chart data
  private updateTeamChart(): void {
    const labels = this.teamStatsRows.map(team => team.team);

    this.teamChartData = {
      labels: labels,
      datasets: [
        {
          label: 'Repositories',
          data: this.teamStatsRows.map(team => team.repositories),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        },
        {
          label: 'Release Pipelines',
          data: this.teamStatsRows.map(team => team.releasePipelines),
          backgroundColor: 'rgba(255, 99, 132, 0.6)',
          borderColor: 'rgba(255, 99, 132, 1)',
          borderWidth: 1
        },
        {
          label: 'Build Definitions',
          data: this.teamStatsRows.map(team => team.buildDefinitions),
          backgroundColor: 'rgba(255, 206, 86, 0.6)',
          borderColor: 'rgba(255, 206, 86, 1)',
          borderWidth: 1
        },
        {
          label: 'YAML Pipelines',
          data: this.teamStatsRows.map(team => team.yamlPipelines),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };
  }

  // Retry loading data
  retryLoad(): void {
    this.loadData();
  }
}
